import React, { useEffect, useState } from 'react';
import { Header } from '../../components/layout/Header';
import { KPICard } from '../../components/dashboard/KPICard';
import { TrendChart, PieChartComponent } from '../../components/dashboard/Charts';
import { reportsApi } from '../../lib/api';
import { toast } from 'sonner';
import { formatNumber, formatDate, formatDateRange } from '../../lib/utils';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { 
    TrendingUp, Eye, Heart, Share2, MessageCircle,
    Users, ExternalLink, FileText, Video, BookOpen, Calendar
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';

const contentTypeIcons = {
    thought_leadership: FileText, company_post: TrendingUp, blog: BookOpen,
    whitepaper: FileText, case_study: FileText, video: Video,
};
const contentTypeColors = {
    thought_leadership: '#FF3B30', company_post: '#0077B5', blog: '#34A853',
    whitepaper: '#F59E0B', case_study: '#8B5CF6', video: '#EA4335',
};

export default function InboundPage() {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [selectedReportId, setSelectedReportId] = useState('');
    const [hasData, setHasData] = useState(false);

    useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        try {
            const response = await reportsApi.getAll();
            setReports(response.data);
            if (response.data.length > 0) {
                setSelectedReportId(response.data[0].id);
                setHasData(true);
            }
        } catch (error) {
            toast.error('Failed to load inbound data');
        } finally {
            setLoading(false);
        }
    };

    const selectedReport = reports.find(r => r.id === selectedReportId);
    const data = selectedReport?.inbound || {};

    const trendData = reports.slice(0, 8).reverse().map((report, index) => ({
        name: `W${index + 1}`,
        views: Math.round((report.inbound?.total_views || 0) / 100),
        engagement: report.inbound?.total_engagement || 0,
        leads: report.inbound?.total_leads || 0,
    }));

    const contents = data?.contents || [];
    const contentTypes = contents.reduce((acc, c) => { acc[c.type] = (acc[c.type] || 0) + 1; return acc; }, {});
    const contentDistribution = Object.entries(contentTypes).map(([name, value]) => ({
        name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()), value,
    }));

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-channel-inbound/30 border-t-channel-inbound rounded-full animate-spin" />
            </div>
        );
    }

    if (!hasData) {
        return (
            <div className="min-h-screen" data-testid="inbound-page">
                <Header title="Inbound Performance" subtitle="Content engagement and lead generation analytics" />
                <div className="p-8">
                    <div className="card-base p-12 text-center">
                        <TrendingUp className="w-16 h-16 text-content-muted mx-auto mb-4" />
                        <h3 className="text-xl font-heading font-bold text-white mb-2">No Inbound Data Available</h3>
                        <p className="text-content-secondary max-w-md mx-auto">Inbound metrics will appear here once your admin team adds report data.</p>
                    </div>
                </div>
            </div>
        );
    }

    const inbound = data;

    return (
        <div className="min-h-screen" data-testid="inbound-page">
            <Header title="Inbound Performance" subtitle="Content engagement and lead generation analytics" />

            <div className="p-8 space-y-8">
                {/* Week Selector */}
                <div className="flex items-center gap-4" data-testid="inbound-week-selector">
                    <Calendar className="w-5 h-5 text-content-muted" />
                    <Select value={selectedReportId} onValueChange={setSelectedReportId}>
                        <SelectTrigger className="input-base w-80">
                            <SelectValue placeholder="Select period" />
                        </SelectTrigger>
                        <SelectContent className="bg-surface-card border-line-default max-h-60">
                            {reports.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                    {formatDateRange(r.period_start, r.period_end)} ({r.report_type})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* KPI Cards */}
                <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard title="Total Views" value={inbound.total_views || 0} icon={Eye} color="inbound" data-testid="inbound-views-kpi" />
                    <KPICard title="Total Engagement" value={inbound.total_engagement || 0} icon={Heart} data-testid="inbound-engagement-kpi" />
                    <KPICard title="Leads Generated" value={inbound.total_leads || 0} icon={Users} color="red" data-testid="inbound-leads-kpi" />
                    <KPICard title="Content Pieces" value={contents.length} icon={FileText} data-testid="inbound-content-count-kpi" />
                </section>

                {/* Charts */}
                {(trendData.some(d => d.views > 0 || d.engagement > 0) || contentDistribution.length > 0) && (
                    <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {trendData.some(d => d.views > 0 || d.engagement > 0) && (
                            <TrendChart data={trendData} dataKey="views" title="VIEWS TREND (100s)" color="#34A853" />
                        )}
                        {contentDistribution.length > 0 && (
                            <PieChartComponent data={contentDistribution} title="CONTENT TYPE DISTRIBUTION" />
                        )}
                    </section>
                )}

                {/* Content List */}
                {contents.length > 0 && (
                    <section className="card-base p-6" data-testid="inbound-content-list">
                        <h3 className="text-lg font-heading font-bold text-white mb-6">CONTENT ASSETS</h3>
                        <div className="space-y-4">
                            {contents.map((content) => {
                                const Icon = contentTypeIcons[content.type] || FileText;
                                const color = contentTypeColors[content.type] || '#FF3B30';
                                return (
                                    <div key={content.id} className="flex items-center gap-4 p-4 rounded-lg bg-surface-app border border-line-default hover:border-brand-red/30 transition-colors" data-testid={`content-${content.id}`}>
                                        <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
                                            <Icon className="w-5 h-5" style={{ color }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h4 className="font-medium text-white truncate">{content.title}</h4>
                                                <a href={content.link} target="_blank" rel="noopener noreferrer" className="text-content-muted hover:text-brand-red">
                                                    <ExternalLink className="w-4 h-4" />
                                                </a>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-content-muted">
                                                <Badge className="text-xs" style={{ backgroundColor: `${color}20`, color }}>{content.type.replace('_', ' ')}</Badge>
                                                <span>{formatDate(content.date_published)}</span>
                                            </div>
                                        </div>
                                        <div className="hidden md:flex items-center gap-6">
                                            {[
                                                { icon: Eye, val: formatNumber(content.views), label: 'Views' },
                                                { icon: Heart, val: content.likes, label: 'Likes' },
                                                { icon: Share2, val: content.shares, label: 'Shares' },
                                                { icon: MessageCircle, val: content.comments, label: 'Comments' },
                                                { icon: Users, val: content.leads_generated, label: 'Leads', accent: true },
                                            ].map(({ icon: I, val, label, accent }) => (
                                                <div key={label} className="text-center">
                                                    <div className={`flex items-center gap-1 ${accent ? 'text-brand-red' : 'text-content-secondary'}`}>
                                                        <I className="w-4 h-4" />
                                                        <span className={`text-sm ${accent ? 'font-bold' : 'font-medium text-white'}`}>{val}</span>
                                                    </div>
                                                    <span className="text-xs text-content-muted">{label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
